import React, { useEffect, useMemo, useState } from "react";
import { Template } from "./interfaces/template.interface";
import "./LeftSidebar.css";
import { ActionType, toCamelCase } from "../utils/common.utils";
import { fetchTemplates } from "../store/templatesSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";

type TemplateTab = "MY" | "PUBLIC";

interface LeftSidebarProps {
  actionType: ActionType;
  onTemplateSelect: (template: Template) => void;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({ onTemplateSelect, actionType }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { templates } = useSelector((state: RootState) => state.templates);

  const [activeTab, setActiveTab] = useState<TemplateTab>("MY");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  /** Split templates */
  const myTemplates = useMemo(
    () => templates.filter((t) => !t.isPublic),
    [templates]
  );

  const publicTemplates = useMemo(
    () => templates.filter((t) => t.isPublic),
    [templates]
  );

  const visibleTemplates = activeTab === "MY" ? myTemplates : publicTemplates;

  /** Auto-select first template when list changes */
  useEffect(() => {
    if (visibleTemplates.length && actionType !== "Edit") {
      handleTemplateSelect(visibleTemplates[0]);
    }
  }, [activeTab, templates]);

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplateId(template._id);
    onTemplateSelect(template);
  };

  return (
    <aside className="left-sidebar p-3">
      {/* Header */}
      <div className="mb-3">
        <div className="fw-semibold">Templates</div>
        <div className="text-muted small">
          {activeTab === "MY"
            ? "Your editable templates"
            : "Read-only public templates"}
        </div>
      </div>

      {/* Tabs */}
     <ul className="nav nav-pills nav-fill mb-3 bg-white border rounded-pill p-1 shadow-sm">
  <li className="nav-item">
    <button
      className={`nav-link rounded-pill d-flex align-items-center justify-content-center gap-2 ${
        activeTab === "MY" ? "active" : ""
      }`}
      onClick={() => setActiveTab("MY")}
      type="button"
    >
      <i className="fa-solid fa-user-gear" />
      My Templates
      <span className={`badge rounded-pill ${activeTab === "MY" ? "bg-light text-dark" : "bg-secondary-subtle text-dark"}`}>
        {myTemplates.length}
      </span>
    </button>
  </li>

  <li className="nav-item">
    <button
      className={`nav-link rounded-pill d-flex align-items-center justify-content-center gap-2 ${
        activeTab === "PUBLIC" ? "active" : ""
      }`}
      onClick={() => setActiveTab("PUBLIC")}
      type="button"
    >
      <i className="fa-solid fa-globe" />
      Public
      <span className={`badge rounded-pill ${activeTab === "PUBLIC" ? "bg-light text-dark" : "bg-secondary-subtle text-dark"}`}>
        {publicTemplates.length}
      </span>
    </button>
  </li>
</ul>


      {/* List */}
      <div className="template-list">
        {visibleTemplates.map((template) => (
          <div onClick={() => handleTemplateSelect(template)} style={{ backgroundColor: selectedTemplateId === template._id ? "#b8b8f7" : "initial" }} key={template._id} className="template-card">
            <div className="fw-semibold text-dark text-truncate text-left" title={template.title}>
              {template.title}
            </div>
            <div
              style={{
                ...template.draft?.body?.inLineStyle?.reduce((styleObj: any, style) => {
                  const camelCaseProp = toCamelCase(style.prop);
                  styleObj[camelCaseProp] = style.value;
                  return styleObj;
                }, {}),
              }}
            >

              <div className="col-md-12">
                <table >
                  <thead className="bg-gray-700">
                    <tr style={{ border: "1px solid" }}>

                      {template?.draft?.columns?.slice(0, 4).map((col, index) => (
                        <th
                          key={col.field}
                          className={col.header?.cssClass || ""}
                          style={{
                            ...col.header?.inLineStyle?.reduce((styleObj: any, style) => {
                              const camelCaseProp = toCamelCase(style.prop);
                              styleObj[camelCaseProp] = style.value;
                              return styleObj;
                            }, {}),
                          }}
                        >
                          {(col.header?.label?.length > 6 ? col.header.label.substring(0, 6) + "..." : col.header?.label) || ""}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {template?.draft?.columns?.slice(0, 4).map((col, index) => (
                        <th
                          key={col.field}
                          className={col.body?.cssClass || ""}
                          style={{
                            ...col.body?.inLineStyle?.reduce((styleObj: any, style) => {
                              const camelCaseProp = toCamelCase(style.prop);
                              styleObj[camelCaseProp] = style.value;
                              return styleObj;
                            }, {}),
                          }}
                        >
                          -
                        </th>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="d-flex align-items-start justify-content-between gap-2">
              {/* Title + badges */}
              <div className="flex-grow-1">
                <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                  <span className="badge rounded-pill text-bg-primary">
                    {template.tripType}
                  </span>

                  <span
                    className={`badge rounded-pill ${template.status === "PUBLISHED" ? "text-bg-success" : "text-bg-secondary"
                      }`}
                  >
                    {template.status}
                  </span>
                </div>
              </div>

              {/* Optional: small chevron/action hint */}
              <div className="text-muted small pt-1">
                <i className="fa-solid fa-chevron-right" />
              </div>
            </div>

          </div>
        ))}
      </div>
    </aside>
  );
};

export default LeftSidebar;